/**
 * TopNav.tsx -- Full canonical composition for the Capicua top navigation bar.
 *
 * Three zones: logo left / 4-item menu centre / utility cluster right.
 * Sticky, 64px tall on desktop, 1px hairline bottom border.
 * Hidden on Sign-in and OAuth-callback routes (handled by PageShell/AuthGate).
 * Shows a 1px deep-green top accent stripe on /admin/* routes.
 *
 * LOAD-BEARING wordmark structure:
 *   Capic<span position:relative>u</span>a<span.dot aria-hidden />
 *   The coral dot is a SIBLING span placed AFTER the closing "a" -- NOT the
 *   diacritic on the "i". Dot styling is INLINE so unit tests can read
 *   style.transform and style.background from jsdom (which does not load CSS).
 *
 * LOAD-BEARING primary menu -- exactly 4 items in canonical order:
 *   Command Center, Workspaces, Activity, Library
 *
 * Utility cluster (right zone), in order:
 *   role-tag chip, search button, DecisionsTray, NotificationBellTrigger, avatar dropdown
 *
 * @see specs/integration-plan.md Section 4.3
 * @see specs/prd.md Section 3 FR-007, FR-008, FR-009
 */
import React, { useMemo, useState, useCallback } from "react";
import { Link, useHashLocation } from "../../lib/router";
import { useRouterContext } from "../../lib/router/Router";
import { useCurrentUserId, useFeatureFlag } from "../../api/hooks";
import { useCurrentWorkspace } from "../../api/hooks/useCurrentWorkspace";
import { useRepositoryList } from "../../api/hooks/useRepositoryList";
import { useCommandPalette } from "../providers/CommandPaletteProvider";
import { useToastConfirm } from "../cross-cutting/useToastConfirm";
import { usePrefetchRoute } from "../../lib/usePrefetchRoute";
import { getPrefetchFactory } from "../../lib/routePrefetch";
import { Dropdown, Icon } from "../primitives";
import { DecisionsTray } from "./DecisionsTray";
import { NotificationBellTrigger } from "./NotificationBellTrigger";
import { TopNavMobile } from "./TopNavMobile";
import SubscriptionScope from "./SubscriptionScope";
import { useT } from "../../i18n/useT";

/** Capicua wordmark prefix — not a translatable string. */
const WORDMARK_PREFIX = "Capic";

// ---------------------------------------------------------------------------
// Menu item type + canonical menu definition
// ---------------------------------------------------------------------------

interface MenuItem {
  id: string;
  label: string;
  href: string;
  match: (route: string) => boolean; // eslint-disable-line no-unused-vars
}

const MENU_ITEMS: MenuItem[] = [
  {
    id: "command-center",
    label: "Command Center",
    href: "/today",
    match: (r) =>
      r === "/" ||
      r.startsWith("/today"),
  },
  {
    id: "workspaces",
    label: "Workspaces",
    href: "/workspaces",
    match: (r) => r.startsWith("/workspaces") || r.startsWith("/workspace/"),
  },
  {
    id: "activity",
    label: "Activity",
    href: "/activity",
    match: (r) => r.startsWith("/activity"),
  },
  {
    id: "library",
    label: "Library",
    href: "/library",
    match: (r) =>
      r.startsWith("/library") ||
      r.startsWith("/playbooks") ||
      r.startsWith("/docs/"),
  },
];

// ---------------------------------------------------------------------------
// Avatar dropdown items
// ---------------------------------------------------------------------------

const AVATAR_ITEMS = [
  { key: "account", label: "Account", href: "/me/settings" },
  { key: "linked-ai", label: "Linked AI account", href: "/me/linked" },
  { key: "access-keys", label: "Access keys", href: "/keys" },
  { key: "api-docs", label: "API documentation", href: "/docs/reference" },
  {
    key: "delete-data",
    label: "Delete my data",
    href: "/admin/delete-data",
    divider: true,
  },
  { key: "sign-out", label: "Sign out" },
];

// ---------------------------------------------------------------------------
// PrefetchNavLink — wraps a Link with usePrefetchRoute handlers
// ---------------------------------------------------------------------------

function PrefetchNavLink({
  item,
  isActive,
}: {
  item: MenuItem;
  isActive: boolean;
}) {
  const factory = getPrefetchFactory(item.href);
  const prefetch = usePrefetchRoute(factory);

  return (
    <Link
      href={item.href}
      className={isActive ? "active" : undefined}
      style={{ position: "relative", textDecoration: "none" }}
      onPointerEnter={prefetch.onPointerEnter}
      onPointerLeave={prefetch.onPointerLeave}
      onFocus={prefetch.onFocus}
      onBlur={prefetch.onBlur}
    >
      {item.label}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// TopNavWorkspaceSwitcher — compact workspace selector for the top navigation.
//
// Mirrors the SideRail WorkspaceSwitcher's wiring (T313 / Fix B): items come
// from useRepositoryList, selection routes through useRouterContext().navigate
// (never window.location.hash), and the trigger label reflects the current
// workspace resolved by useCurrentWorkspace. Mounted only when the
// `workspace_context_v1` feature flag is enabled.
// ---------------------------------------------------------------------------

function TopNavWorkspaceSwitcher(): React.ReactElement {
  const t = useT();
  const workspace = useCurrentWorkspace();
  const { navigate } = useRouterContext();
  const repos = useRepositoryList();

  const name =
    workspace.status === "ready" ? workspace.data?.name ?? "" : "";

  const items = useMemo(() => {
    if (repos.status !== "ready") return [];
    return repos.data.map((w) => ({
      key: w.id,
      label: w.name,
      onClick: () => {
        navigate(`/workspace/${w.slug}`);
      },
    }));
  }, [repos, navigate]);

  return (
    <Dropdown
      trigger={
        <button
          type="button"
          data-testid="topnav-workspace-switcher"
          aria-label={`Workspace: ${name || t("component.loading")}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-xs, 6px)",
            background: "transparent",
            border: "1px solid var(--bg2, #eeece7)",
            borderRadius: "var(--radius-sm, 6px)",
            padding: "var(--space-xxs, 2px) var(--space-sm, 8px)",
            cursor: "pointer",
            fontFamily: "var(--font-ui)",
            fontSize: "var(--fs-caption, 14px)",
            color: "var(--fg1)",
          }}
        >
          <span>{name || t("component.loading-ellipsis")}</span>
          <span aria-hidden="true">{"▾"}</span>
        </button>
      }
      items={items}
      align="start"
      ariaLabel="Workspace switcher"
    />
  );
}

// ---------------------------------------------------------------------------
// TopNav component
// ---------------------------------------------------------------------------

export function TopNav() {
  const [path] = useHashLocation();
  const userId = useCurrentUserId();
  const notifEnabled = useFeatureFlag("notifications_inbox_v1");
  const workspaceContextEnabled = useFeatureFlag("workspace_context_v1");
  const { open: openPalette } = useCommandPalette();
  const showWithUndo = useToastConfirm();

  // Decision count from the DecisionsTray callback
  const [decisionCount, setDecisionCount] = useState(0);

  const isAdmin = path.startsWith("/admin");

  // Sign-out handler (triggers confirmation toast then performs sign-out)
  const handleSignOut = useCallback(() => {
    showWithUndo({
      onConfirm: () => {
        // Sign-out mutation would go here; wired by auth layer
      },
    });
  }, [showWithUndo]);

  // Build dropdown items with sign-out onClick handler
  const avatarDropdownItems = AVATAR_ITEMS.map((item) => {
    if (item.key === "sign-out") {
      return { ...item, onClick: handleSignOut };
    }
    return item;
  });

  return (
    <header
      data-testid="top-nav"
      style={{
        position: "sticky",
        top: 0,
        background: "var(--bg1, #ffffff)",
        zIndex: 50,
        ...(isAdmin
          ? { borderTop: "1px solid var(--bg-deep-green, #003c33)" }
          : {}),
      }}
    >
      {/* ── Desktop variant (visible >=768px, hidden <768px via responsive.css) ── */}
      <div
        className="topnav__desktop"
        style={{
          height: "64px",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid var(--bg2, #eeece7)",
          padding: "0 var(--space-xl, 24px)",
        }}
      >
        {/* ── Left zone: Wordmark ── */}
        <span className="brand" aria-label="Capicua home">
          {WORDMARK_PREFIX}
          <span style={{ position: "relative" }}>{"u\u0301"}</span>
          {"a"}
          <span
            className="dot"
            aria-hidden="true"
            style={{
              display: "inline-block",
              width: 5,
              height: 5,
              background: "var(--color-coral)",
              marginLeft: 1,
              transform: "translateY(-12px)",
            }}
          />
        </span>

        {/* ── Centre zone: 4-item primary menu + workspace switcher ── */}
        <nav
          aria-label="Primary"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-xl, 24px)",
          }}
        >
          {/* Workspace switcher (flag-gated by workspace_context_v1) — mounted
              on every non-authentication route so global-navigation surfaces
              that mount no SideRail still expose a workspace selector. */}
          {workspaceContextEnabled === true && <TopNavWorkspaceSwitcher />}
          {MENU_ITEMS.map((item) => (
            <PrefetchNavLink
              key={item.id}
              item={item}
              isActive={item.match(path)}
            />
          ))}
        </nav>

        {/* ── Right zone: Utility cluster ── */}
        <div
          style={{ display: "flex", alignItems: "center", gap: "var(--space-md, 12px)" }}
        >
          {/* Role-tag chip */}
          <span
            style={{
              fontFamily: "var(--font-mono, monospace)",
              fontSize: "var(--fs-micro, 12px)",
              textTransform: "uppercase",
              border: "1px solid var(--fg4, #93939f)",
              borderRadius: "var(--radius-pill, 32px)",
              padding: "var(--space-xxs, 2px) var(--space-sm, 8px)",
              letterSpacing: "0.05em",
            }}
          >
            Operator
          </span>

          {/* Search / command palette button */}
          <button
            type="button"
            aria-label="Search"
            onClick={openPalette}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              padding: "var(--space-xs, 6px)",
            }}
          >
            <Icon name="search" size={18} />
          </button>

          {/* DecisionsTray + badge */}
          <DecisionsTray onCountChange={setDecisionCount} />
          {decisionCount > 0 && (
            <span aria-label={`${decisionCount} pending decisions`}>
              {decisionCount > 5 ? "5+" : decisionCount}
            </span>
          )}

          {/* Notifications bell -- feature-flagged, subscription-scoped (FR-007 / FR-009) */}
          {notifEnabled === true && userId != null && (
            <SubscriptionScope key={userId} active>
              <NotificationBellTrigger />
            </SubscriptionScope>
          )}

          {/* Avatar dropdown */}
          <Dropdown
            trigger={
              <button
                type="button"
                data-testid="avatar-trigger"
                aria-label="User menu"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "var(--fg3, #75758a)",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--fg-on-dark, #ffffff)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "var(--fs-caption, 14px)",
                  fontWeight: 600,
                }}
              >
                <Icon name="user" size={16} />
              </button>
            }
            items={avatarDropdownItems}
            align="end"
            ariaLabel="User menu"
          >
            {avatarDropdownItems.map((item) => (
              <React.Fragment key={item.key}>
                {item.divider && <hr role="separator" />}
                {item.onClick ? (
                  <button type="button" onClick={item.onClick}>{item.label}</button>
                ) : (
                  <Link href={item.href ?? "#"}>{item.label}</Link>
                )}
              </React.Fragment>
            ))}
          </Dropdown>
        </div>
      </div>

      {/* ── Mobile variant (visible <768px, hidden >=768px via responsive.css) ── */}
      <div className="topnav__mobile">
        <TopNavMobile />
      </div>
    </header>
  );
}
